import type { TeamFinderFilters } from './types'

/**
 * URL query keys for the filter form, so a search can be shared as a link.
 *
 * Short, readable names rather than the internal field names — these are
 * user-visible. Every filter round-trips: `sport`/`generic_sport` are uids,
 * `gender`/`position`/`province_id` are numeric ids, and the rest are free
 * text. Country/province travel as NAMES because that is what the form stores
 * and what `CountryAndProvinceInput` reads back.
 */
export const FILTER_PARAM_KEYS: Record<keyof TeamFinderFilters, string> = {
  genderId: 'gender',
  sportId: 'sport',
  genericSportId: 'generic_sport',
  positionIds: 'position',
  country: 'country',
  countryId: 'country_id',
  province: 'province',
  provinceId: 'province_id',
  city: 'city',
  team: 'team',
  organization: 'organization',
}

export const EMPTY_FILTERS: TeamFinderFilters = {
  genderId: '',
  sportId: '',
  genericSportId: '',
  positionIds: [],
  country: '',
  countryId: '',
  province: '',
  provinceId: '',
  city: '',
  team: '',
  organization: '',
}

/**
 * Filters → query-string pairs. Unset filters are omitted entirely rather than
 * written as empty values, so a shared link carries only what was actually
 * searched and an unfiltered search leaves a clean URL.
 */
export const filtersToParams = (
  filters: TeamFinderFilters
): Record<string, string> => {
  const params: Record<string, string> = {}
  ;(Object.keys(FILTER_PARAM_KEYS) as (keyof TeamFinderFilters)[]).forEach(
    (field) => {
      const raw = filters[field]
      // List fields travel as a comma list, matching how they go to the API —
      // one format for the same value in both places.
      const value = Array.isArray(raw)
        ? raw.filter(Boolean).join(',')
        : raw?.trim()
      if (value) {
        params[FILTER_PARAM_KEYS[field]] = value
      }
    }
  )
  return params
}

/**
 * Query string → filters, filling anything absent from the empty defaults.
 *
 * Deliberately tolerant: an unknown or malformed param is ignored rather than
 * rejected. A hand-edited or truncated link should still open a usable form,
 * not an error — the API is what validates the values.
 */
export const paramsToFilters = (search: string): TeamFinderFilters => {
  const query = new URLSearchParams(search)
  const filters = { ...EMPTY_FILTERS }
  ;(Object.keys(FILTER_PARAM_KEYS) as (keyof TeamFinderFilters)[]).forEach(
    (field) => {
      const value = query.get(FILTER_PARAM_KEYS[field])
      if (!value) {
        return
      }
      if (Array.isArray(EMPTY_FILTERS[field])) {
        // Split the comma list back out, dropping the empties a hand-edited
        // link can leave behind ("9,,11" / trailing commas).
        const list = value
          .split(',')
          .map((v) => v.trim())
          .filter(Boolean)
        ;(filters[field] as string[]) = list
        return
      }
      ;(filters[field] as string) = value.trim()
    }
  )
  return filters
}

/** True when at least one filter is set — i.e. the URL describes a search. */
export const hasAnyFilter = (filters: TeamFinderFilters): boolean =>
  Object.values(filters).some((value) =>
    Array.isArray(value) ? value.length > 0 : Boolean(value?.trim())
  )
